/** B (opdracht Frank 23 oktober 2026, na de glasronde van 0.9.193): de Postbus in twee
 *  stromen. Alles hier is puur, zodat de keuzes op uitkomst te toetsen zijn.
 *
 *  - B2: twee tabbladen, Vandaag en Geheugen, elk met een eigen teller.
 *  - B3: per tabblad "Alles gelezen verwijderen": wat gelezen is en geen besluit meer vraagt.
 *  - B4: alleen de ochtendronde van vandaag bovenaan; oudere ingeklapt onder Eerdere rondes.
 *  - B5: de kop volgt de taal van de app, niet de taal van het moment waarop het bericht ontstond.
 */
import { VAULTORDE, kopVanVault, kopVanVerandering } from "./vaultorde";

export const VANDAAG = "vandaag";
export const GEHEUGEN = "geheugen";
const RONDE = "ochtendronde";

/** In welk tabblad een kaart staat. De backend zegt het (`stroom`); een oudere backend niet. */
export function stroomVan(bericht) {
  if (bericht?.stroom === VANDAAG || bericht?.stroom === GEHEUGEN) return bericht.stroom;
  return bericht?.soort === "geheugenvoorstel" ? GEHEUGEN : VANDAAG;
}

/** De datum van vandaag op deze machine, als JJJJ-MM-DD (de sleutel van een ochtendronde). */
export function vandaagIso(nu = new Date()) {
  const twee = (n) => String(n).padStart(2, "0");
  return `${nu.getFullYear()}-${twee(nu.getMonth() + 1)}-${twee(nu.getDate())}`;
}

/** Hoeveel kaarten in deze stroom op Frank wachten. */
export function teller(berichten, stroom) {
  return (berichten || []).filter((b) => stroomVan(b) === stroom && b.aandacht).length;
}

/** Het tabblad dat opengaat: Vandaag, tenzij alleen Geheugen iets vraagt. */
export function beginStroom(berichten) {
  return teller(berichten, VANDAAG) === 0 && teller(berichten, GEHEUGEN) > 0 ? GEHEUGEN : VANDAAG;
}

/** De kaarten van een tabblad in drie delen: boven, de rest, en eerdere rondes (ingeklapt). */
export function indeling(berichten, stroom, vandaag) {
  const hier = (berichten || []).filter((b) => stroomVan(b) === stroom);
  const ronde = (b) => b.soort === RONDE;
  const boven = hier.find((b) => ronde(b) && b.sleutel === vandaag) || null;
  return {
    boven,
    overig: hier.filter((b) => !ronde(b)),
    eerder: hier.filter((b) => ronde(b) && b !== boven),
  };
}

/** De bericht-ids die "Alles gelezen verwijderen" weghaalt: gelezen en zonder open besluit. */
export function gelezenIds(berichten, stroom) {
  return (berichten || [])
    .filter((b) => stroomVan(b) === stroom && b.gelezen && !b.aandacht)
    .flatMap((b) => b.bericht_ids || [b.id]);
}

const SKILL = "Skill voorstellen: ";
export const NIEUWE_BANK = "nieuwe_kennisbank";
const BANK = "Nieuwe kennisbank: ";
const ADVIES = "Modeladvies: ";

/** De kop van een kaart in de taal van de app. De opgeslagen titel blijft zoals hij is (R2);
 *  een ochtendronde krijgt haar kop uit haar datum, de rest gaat door de vertaling. */
export function kopVan(bericht, t, taal) {
  const titel = bericht?.titel || "";
  const sleutel = bericht?.sleutel || "";
  if (bericht?.soort === RONDE && /^\d{4}-\d{2}-\d{2}$/.test(sleutel)) {
    // Middernacht UTC en de dag in UTC: een kale datum mag nergens een dag verschuiven.
    const d = new Date(`${sleutel}T00:00:00Z`);
    const dag = d.toLocaleDateString(taal === "en" ? "en-GB" : "nl-NL", { weekday: "long", timeZone: "UTC" });
    return t("Ochtendronde {dag} {datum}", { dag, datum: `${d.getUTCDate()}-${d.getUTCMonth() + 1}` });
  }
  // B1 (4 nov): de tussenronde van de proactieve wachter; de kop uit haar tijdstip (de sleutel).
  if (bericht?.soort === "wachtronde" && /T\d{2}:\d{2}$/.test(sleutel)) {
    return t("Tussenronde {tijd}", { tijd: sleutel.slice(-5) });
  }
  if (bericht?.soort === "skillvoorstel" && titel.startsWith(SKILL)) {
    return t("Skill voorstellen: {naam}", { naam: titel.slice(SKILL.length) });
  }
  // QIS (glasronde 0.9.202): een nieuwe vaultmap die vanzelf een kennisbank werd.
  if (bericht?.soort === NIEUWE_BANK && titel.startsWith(BANK)) {
    return t("Nieuwe kennisbank: {naam}", { naam: titel.slice(BANK.length) });
  }
  // ZL4 (27 okt): het modeladvies per taak; de taak gaat apart door de vertaling.
  if (bericht?.soort === "modeladvies" && titel.startsWith(ADVIES)) {
    return t("Modeladvies: {taak}", { taak: t(titel.slice(ADVIES.length)) });
  }
  // Brain 1-op-1 (25 okt): wat de sync met de vault deed.
  const vault = bericht?.soort === VAULTORDE
    ? (kopVanVerandering(bericht?.inhoud?.vault) || kopVanVault(titel)) : null;
  if (vault) return t(vault[0], vault[1]);
  return t(titel);
}
